import type { ParamSpec } from "@cfg/design-sdk";
import { useMemo, useState, type ReactNode } from "react";
import { useDesign, useMesh, useParams } from "./hooks.ts";
import { ParamField } from "./widgets.tsx";

interface Group {
  id: string;
  title: string;
  params: string[];
  collapsed?: boolean;
}

/** Split the spec into the design's declared groups plus an "Other" group for the rest. */
export function groupSpecs(spec: ParamSpec[], groups: Group[] | undefined, order?: string[]): { group: Group; specs: ParamSpec[] }[] {
  const byName = new Map(spec.map((s) => [s.name, s]));
  const used = new Set<string>();
  const out: { group: Group; specs: ParamSpec[] }[] = [];
  for (const g of groups ?? []) {
    const specs = g.params.map((n) => byName.get(n)).filter((s): s is ParamSpec => !!s);
    specs.forEach((s) => used.add(s.name));
    if (specs.length) out.push({ group: g, specs });
  }
  // params that declared a group via meta but were not listed
  for (const s of spec) {
    if (used.has(s.name) || !s.group) continue;
    const groupId = s.group;
    let bucket = out.find((o) => o.group.id === groupId);
    if (!bucket) {
      bucket = { group: { id: groupId, title: groupId.replace(/\b\w/g, (c) => c.toUpperCase()), params: [] }, specs: [] };
      out.push(bucket);
    }
    bucket.specs.push(s);
    used.add(s.name);
  }
  const rest = spec.filter((s) => !used.has(s.name));
  if (rest.length) out.unshift({ group: { id: "main", title: out.length ? "General" : "Parameters", params: rest.map((r) => r.name) }, specs: rest });
  if (order?.length) {
    const rank = new Map(order.map((n, i) => [n, i]));
    for (const o of out) o.specs.sort((a, b) => (rank.get(a.name) ?? 1e9) - (rank.get(b.name) ?? 1e9));
  }
  return out;
}

export interface ParamFormProps {
  /** Hide params flagged `advanced` unless toggled. */
  showAdvanced?: boolean;
  disabled?: boolean;
  /** Rendered above the fields (presets, etc.). */
  header?: ReactNode;
  footer?: ReactNode;
  validationErrors?: Record<string, string>;
}

/** Generated parameter form: groups become collapsible sections, each param its widget. */
export function ParamForm({ showAdvanced = false, disabled, header, footer, validationErrors }: ParamFormProps) {
  const { description, spec } = useDesign();
  const { params, setParam, setActive } = useParams();
  const groups = useMemo(
    () => groupSpecs(spec, description?.ui?.groups as Group[] | undefined, description?.ui?.order as string[] | undefined),
    [spec, description],
  );
  const [open, setOpen] = useState<Record<string, boolean>>({});
  if (!description) return null;
  return (
    <form className="cfg-form" onSubmit={(e) => e.preventDefault()}>
      {header}
      {groups.map(({ group, specs }) => {
        const visible = specs.filter((s) => showAdvanced || !s.advanced);
        if (!visible.length) return null;
        const isOpen = open[group.id] ?? !group.collapsed;
        return (
          <section key={group.id} className={`cfg-group${isOpen ? " is-open" : ""}`}>
            {groups.length > 1 && (
              <button type="button" className="cfg-group-toggle" aria-expanded={isOpen} onClick={() => setOpen({ ...open, [group.id]: !isOpen })}>
                <span>{group.title}</span>
                <span className="cfg-group-chevron" aria-hidden>
                  {isOpen ? "−" : "+"}
                </span>
              </button>
            )}
            {isOpen && (
              <div className="cfg-group-body">
                {visible.map((s) => (
                  <ParamField
                    key={s.name}
                    spec={s}
                    value={params[s.name]}
                    disabled={disabled}
                    invalid={validationErrors?.[s.name]}
                    onChange={(v) => setParam(s.name, v)}
                    onFocus={() => setActive(s.name)}
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}
      {footer}
    </form>
  );
}

/** Preset chips; hidden when the design ships none. */
export function PresetBar({ label = "Presets" }: { label?: string }) {
  const { description } = useDesign();
  const { applyPreset } = useParams();
  const presets = (description?.ui?.presets ?? []) as { id: string; title: string; note?: string }[];
  if (!presets.length) return null;
  return (
    <div className="cfg-presets" role="group" aria-label={label}>
      <span className="cfg-presets-label">{label}</span>
      {presets.map((p) => (
        <button key={p.id} type="button" className="cfg-chip" title={p.note} onClick={() => applyPreset(p.id)}>
          {p.title}
        </button>
      ))}
    </div>
  );
}

/** Print-check issues from the design's validate() and the kernel. */
export function IssueList({ title = "Print check" }: { title?: string }) {
  const issues = useMesh();
  if (!issues.issues.length) return null;
  return (
    <div className="cfg-issues" role="status">
      <div className="cfg-issues-title">{title}</div>
      <ul>
        {issues.issues.map((i, n) => (
          <li key={n} className={`cfg-issue is-${i.level}`} data-param={i.param}>
            {i.message}
          </li>
        ))}
      </ul>
    </div>
  );
}
